// src/components/public_utilities/pu/PuMapInsights.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — "What the map shows" (AI) for the Public Utilities GIS tab
// --- UPDATE (Public Utilities v3 — PU-only map) — NEW FILE ---------------------------------------
//   • summarizeMapLayer(layer) — plain-language findings computed ONLY from the map data the server
//     sent (overdue / due this week, failed or damaged, no manager, outside every constituency,
//     no GPS) with the constituency that has the most of each. No estimates, no invented numbers;
//     a finding with nothing behind it is not shown.
//   • each finding has a one-click action (colour the map by it, open the matching list)
//   • Ask FlowSense: the question goes to /flowsense-ai/ask with this summary as context; when the AI
//     is offline the built-in findings are shown instead (labelled as such)
// --- END UPDATE ------------------------------------------------------------------------------
import React, { memo, useCallback, useMemo, useState } from 'react';
import {
    Box, Flex, HStack, VStack, Text, Button, Icon, Badge, Input, InputGroup, InputRightElement, IconButton, Collapse, Spinner, Tooltip,
} from '@chakra-ui/react';
import { MdPsychology, MdSend, MdExpandMore, MdExpandLess, MdOutlineAutoAwesome } from 'react-icons/md';
import { usePuColors } from './puUtils';
import { askPublicUtilitiesAi } from '../../../services/publicUtilitiesApi';

const n = (x) => Number(x || 0).toLocaleString();
const plural = (k, one, many) => `${n(k)} ${k === 1 ? one : many}`;

function topBy(features, pred) {
    const m = new Map();
    for (const f of features) { if (!pred(f)) continue; const k = f.constituency || 'No constituency'; m.set(k, (m.get(k) || 0) + 1); }
    let best = null; let bestN = 0;
    for (const [k, v] of m) if (v > bestN) { best = k; bestN = v; }
    return best ? { name: best, count: bestN, places: m.size } : null;
}

/** @returns {{ lines: Array<{key,severity,text,action}>, context: object }} */
export function summarizeMapLayer(layer) {
    if (!layer || !Array.isArray(layer.features)) return { lines: [], context: null };
    const f = layer.features;
    const due = layer.legend?.due || {};
    const lines = [];
    const overdue = due.overdue || 0;
    if (overdue) {
        const t = topBy(f, (x) => x.due === 'overdue');
        lines.push({ key: 'overdue', severity: 'critical', text: `${plural(overdue, 'asset is', 'assets are')} overdue for inspection${t ? ` — most in ${t.name} (${n(t.count)})` : ''}.`, action: { type: 'color', colorBy: 'due', label: 'Show on map' }, action2: { type: 'tab', tab: 'inspections', filter: { state: 'overdue' }, label: 'Open list' } });
    }
    const week = due.due_this_week || 0;
    if (week) {
        const t = topBy(f, (x) => x.due === 'due_this_week');
        lines.push({ key: 'week', severity: 'warning', text: `${plural(week, 'inspection is', 'inspections are')} due this week${t ? `; ${t.name} has the most (${n(t.count)})` : ''}.`, action: { type: 'color', colorBy: 'due', label: 'Show on map' }, action2: { type: 'tab', tab: 'inspections', filter: { state: 'due_this_week' }, label: 'Plan route' } });
    }
    const broken = f.filter((x) => x.status === 'failed' || x.status === 'damaged').length;
    if (broken) {
        const t = topBy(f, (x) => x.status === 'failed' || x.status === 'damaged');
        lines.push({ key: 'broken', severity: 'critical', text: `${plural(broken, 'asset is', 'assets are')} failed or damaged${t ? ` — ${t.name} has ${n(t.count)}` : ''}.`, action: { type: 'color', colorBy: 'status', label: 'Show on map' }, action2: { type: 'filter', status: 'damaged', label: 'Open list' } });
    }
    const unassigned = layer.legend?.unassigned_manager || 0;
    if (unassigned) lines.push({ key: 'unmanaged', severity: 'info', text: `${plural(unassigned, 'asset has', 'assets have')} no one responsible yet (MOW+ team, contractor or utility company).`, action: { type: 'color', colorBy: 'manager', label: 'Show on map' }, action2: { type: 'filter', unmanaged: true, label: 'Assign' } });
    const unzoned = layer.legend?.unzoned || 0;
    if (unzoned && (layer.zones || []).some((z) => z.geometry)) lines.push({ key: 'unzoned', severity: 'info', text: `${plural(unzoned, 'asset is', 'assets are')} outside every drawn constituency boundary.`, action: { type: 'tab', tab: 'zones', label: 'Check zones' } });
    else if (unzoned && !(layer.zones || []).some((z) => z.geometry)) lines.push({ key: 'nozones', severity: 'info', text: 'No constituency boundaries are drawn yet — assets are grouped by the constituency code from the inventory.', action: { type: 'tab', tab: 'zones', label: 'Set up zones' } });
    if (layer.no_gps) lines.push({ key: 'nogps', severity: 'info', text: `${plural(layer.no_gps, 'asset is', 'assets are')} not on the map because ${layer.no_gps === 1 ? 'it has' : 'they have'} no GPS.`, action: { type: 'filter', unlocated: true, label: 'Fix GPS' } });
    if (!lines.length && f.length) lines.push({ key: 'ok', severity: 'ok', text: `All ${n(f.length)} mapped assets are scheduled, assigned and not failed. Nothing needs attention on the map.` });

    const byType = (layer.legend?.types || []).slice(0, 6).map((t) => `${t.label}: ${t.count}`).join(', ');
    const context = {
        module: 'public_utilities_map',
        mapped_assets: f.length, without_gps: layer.no_gps || 0, by_type: byType, inspection_state: due,
        failed_or_damaged: broken, no_manager: unassigned, outside_constituencies: unzoned,
        findings: lines.map((l) => l.text),
    };
    return { lines, context };
}

const SEV = { critical: 'red', warning: 'orange', info: 'blue', ok: 'green' };

function PuMapInsights({ layer, onColorBy, onAction }) {
    const c = usePuColors();
    const { lines, context } = useMemo(() => summarizeMapLayer(layer), [layer]);
    const [open, setOpen] = useState(true);
    const [q, setQ] = useState('');
    const [answer, setAnswer] = useState(null);
    const [asking, setAsking] = useState(false);

    const run = useCallback((a) => {
        if (!a) return;
        if (a.type === 'color') onColorBy?.(a.colorBy);
        else onAction?.(a);
    }, [onColorBy, onAction]);

    const ask = useCallback(async () => {
        const text = q.trim();
        if (!text) return;
        setAsking(true); setAnswer(null);
        try {
            const r = await askPublicUtilitiesAi(text, context);
            setAnswer({ text: r?.answer || r?.text || 'No answer returned.', source: 'ai' });
        } catch (e) {
            setAnswer({ text: lines.map((l) => `• ${l.text}`).join('\n'), source: 'built-in', error: e.message });
        } finally { setAsking(false); }
    }, [q, context, lines]);

    if (!layer) return null;
    return (
        <Box mt={3} p={3} borderRadius="xl" borderWidth="1px" borderColor={c.border} bg={c.subtleBg} role="region" aria-label="What the map shows">
            <Flex align="center" gap={2}>
                <Icon as={MdPsychology} color="purple.400" />
                <Text fontWeight="800" fontSize="sm">What the map shows</Text>
                <Tooltip hasArrow label="Computed from the assets on this map — real schedules, statuses and assignments. Nothing is estimated.">
                    <Badge colorScheme="purple" variant="subtle" cursor="help">AI · from your data</Badge>
                </Tooltip>
                <IconButton ml="auto" size="xs" variant="ghost" aria-label={open ? 'Collapse map insights' : 'Expand map insights'} icon={<Icon as={open ? MdExpandLess : MdExpandMore} />} onClick={() => setOpen((v) => !v)} />
            </Flex>
            <Collapse in={open} animateOpacity>
                <VStack align="stretch" spacing={1.5} mt={2}>
                    {lines.map((l) => (
                        <Flex key={l.key} gap={2} align="center" wrap="wrap" data-testid={`map-insight-${l.key}`}>
                            <Badge colorScheme={SEV[l.severity]} minW="8px" h="8px" p={0} borderRadius="full" />
                            <Text fontSize="xs" flex={1} minW="200px">{l.text}</Text>
                            {l.action && <Button size="xs" variant="ghost" colorScheme="teal" onClick={() => run(l.action)}>{l.action.label}</Button>}
                            {l.action2 && <Button size="xs" variant="ghost" colorScheme="purple" onClick={() => run(l.action2)}>{l.action2.label}</Button>}
                        </Flex>
                    ))}
                </VStack>
                <HStack mt={3} spacing={2}>
                    <InputGroup size="sm">
                        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
                            placeholder="Ask FlowSense about the map, e.g. “Where should crews go first this week?”" borderRadius="full" aria-label="Ask FlowSense about the map" />
                        <InputRightElement>
                            {asking ? <Spinner size="xs" /> : <IconButton size="xs" variant="ghost" icon={<Icon as={MdSend} />} aria-label="Ask" onClick={ask} isDisabled={!q.trim()} />}
                        </InputRightElement>
                    </InputGroup>
                </HStack>
                {answer && (
                    <Box mt={2} p={2.5} borderRadius="lg" bg={c.cardBg} borderWidth="1px" borderColor={c.border}>
                        <HStack spacing={1.5} mb={1}>
                            <Icon as={MdOutlineAutoAwesome} color="purple.400" />
                            <Text fontSize="xs" fontWeight="800">{answer.source === 'ai' ? 'FlowSense AI' : 'FlowSense AI is offline — what the map data says'}</Text>
                        </HStack>
                        <Text fontSize="xs" whiteSpace="pre-wrap">{answer.text}</Text>
                    </Box>
                )}
            </Collapse>
        </Box>
    );
}

export default memo(PuMapInsights);
